import React, { useCallback, useState, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { api } from '../utils/apiHelper';
import UserContext from '../context/UserContext';
import '../styles/FarmerTv.css';

const FarmerTv = () => {
  const { authUser } = useContext(UserContext);

  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadToS3 = async (file, projectId, userId) => {
    const signedRes = await api('/farmertv-s3-url', 'POST', {
      fileName: file.name,
      fileType: file.type,
      accessType: 'public-read',
      userId,
      projectId,
    });

    if (!signedRes?.url || !signedRes?.key) {
      throw new Error('Failed to get signed URL');
    }

    await fetch(signedRes.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    return {
      fileUrl: signedRes.url.split('?')[0],
      key: signedRes.key,
    };
  };

  const handleUpload = useCallback(async (file) => {
    try {
      setUploading(true);
      const projectId = uuidv4();
      const fileId = uuidv4();
      const fileIdent = uuidv4();

      const { fileUrl, key } = await uploadToS3(file, projectId, authUser._id);

      let coverKey = null;
      let coverUrl = null;

      if (coverImage) {
        const coverUpload = await uploadToS3(coverImage, projectId, authUser._id);
        coverKey = coverUpload.key;
        coverUrl = coverUpload.fileUrl;
      }

      const fileData = {
        key,
        fileName: file.name,
        url: fileUrl,
        projectId,
        fileId,
        fileIdent,
        fileTitle,
        fileDescription,
        createdAt: new Date().toISOString(),
        userName: authUser?.username || 'unknown',
        coverImage: coverKey || null,
        coverUrl: coverUrl || null,
        userId: authUser?._id || null,
      };

      await api('/save-farmertv-meta', 'POST', fileData);
      alert(`File "${file.name}" uploaded successfully`);
      setFileTitle('');
      setFileDescription('');
      setCoverImage(null);
    } catch (err) {
      console.error('Upload error:', err.message);
      alert('Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [authUser, coverImage, fileTitle, fileDescription]);

  const onDropFile = useCallback((acceptedFiles) => {
    if (!fileTitle || !fileDescription) {
      alert('Please enter title and description first.');
      return;
    }
    acceptedFiles.forEach(handleUpload);
  }, [fileTitle, fileDescription, handleUpload]);

  const onDropCover = useCallback((acceptedFiles) => {
    setCoverImage(acceptedFiles[0]);
  }, []);

  const { getRootProps: getFileRootProps, getInputProps: getFileInputProps } = useDropzone({ onDrop: onDropFile });
  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps } = useDropzone({
    onDrop: onDropCover,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/webp': [],
    },
    multiple: false,
  });

  return (
    <div className="farmer-tv-container">
      <h2>FarmerTV Uploader</h2>

      <input
        type="text"
        placeholder="Enter file title"
        value={fileTitle}
        onChange={(e) => setFileTitle(e.target.value)}
        className="form-input"
      />

      <textarea
        placeholder="Enter file description (max 360 characters)"
        value={fileDescription}
        onChange={(e) => {
          if (e.target.value.length <= 360) {
            setFileDescription(e.target.value);
          }
        }}
        className="form-textarea"
      />

      <div {...getCoverRootProps()} className="farmer-dropzone">
        <input {...getCoverInputProps()} />
        <p>{coverImage ? `Selected Cover: ${coverImage.name}` : 'Click or drag a cover image (jpg/png)'}</p>
      </div>

      <div {...getFileRootProps()} className="farmer-dropzone">
        <input {...getFileInputProps()} />
        <p>{uploading ? 'Uploading...' : 'Drop your file here or click to upload'}</p>
      </div>
    </div>
  );
};

export default FarmerTv;
